"use client";

import { useEffect } from "react";
import {
  isChunkLoadError,
  recoverChunkLoadFailure,
  clearChunkReloadFlagAfter,
} from "@/libs/chunk-load-recovery";

// Captures ALL uncaught client-side errors — event-handler throws, async
// promise rejections, failed fetches in handlers — and reports them to
// /api/error-report, which emails/alerts via notifyError(). Without this,
// only React *render* crashes (app/global-error.tsx) would be reported,
// leaving the much more common runtime client errors invisible.
//
// Client-side throttle: at most one report per message per 60s, so a
// repeating client error doesn't spam the endpoint. Server-side notifyError
// also throttle per fingerprint (15 min), so this is defense in depth.
export default function GlobalErrorHandler(): null {
  useEffect(() => {
    const reported = new Map<string, number>();
    const THROTTLE_MS = 60_000;

    const report = (context: string, message: string, stack?: string) => {
      try {
        // Ignore errors that originate entirely from a browser extension
        // (MetaMask and other wallet injectors, password managers, ad
        // blockers, etc.). These are third-party: injected into our page
        // context but not caused by our code, so we can neither fix nor act
        // on them. Heuristic: the stack references an extension protocol AND
        // no real http(s) origin (our own scripts are always served over
        // https). A mixed stack that also contains our origin is still
        // reported, since our code is in the call chain and may be actionable.
        const EXTENSION_PROTOCOL =
          /(chrome-extension|moz-extension|safari-web-extension|edge-extension):\/\//i;
        const combined = `${stack || ""}\n${message || ""}`;
        if (EXTENSION_PROTOCOL.test(combined) && !/https?:\/\//i.test(combined)) {
          return;
        }

        // Extension messaging-API errors. runtime.sendMessage / chrome.runtime
        // (and the moz/browser/safari equivalents) are the extension-to-
        // extension messaging APIs — a web app never calls them, so any error
        // mentioning them is an injected extension's (e.g. the recurring
        // "Invalid call to runtime.sendMessage(). Tab not found."). These slip
        // past the EXTENSION_PROTOCOL check above because the message carries
        // the API NAME, not an extension:// protocol URL. Same conservatism as
        // above: if our own https origin is in the stack, our code may be in
        // the call chain, so still report.
        const EXTENSION_RUNTIME_API =
          /\b(?:chrome|moz|browser|safari)\.runtime\b|runtime\.sendMessage\b/i;
        if (
          EXTENSION_RUNTIME_API.test(combined) &&
          !/https?:\/\//i.test(combined)
        ) {
          return;
        }

        // Transient chunk/module-load failures ("Loading chunk N failed",
        // stale bundle after a deploy, flaky connection) self-heal on refresh.
        // Recover silently by reloading once instead of reporting. If we
        // already reloaded and it still failed (genuine broken deploy),
        // recoverChunkLoadFailure() returns false and we report normally.
        if (isChunkLoadError(message, stack)) {
          if (recoverChunkLoadFailure()) return;
        }

        // React hydration errors (#418–#425). These fire when the
        // server-rendered HTML doesn't match the client's first render. In
        // production they are almost always caused by a browser extension
        // (Google Translate is the textbook trigger for #419; also Grammarly,
        // dark readers, password managers) mutating DOM text/attributes before
        // React hydrates. React recovers by discarding the server tree and
        // re-rendering client-side, so the page keeps working and there is no
        // application code to fix. Reporting them only generates alert noise
        // for an unactionable, self-healing event. The one deterministic seed
        // we own (the footer copyright year, which can flip at midnight UTC
        // crossings on New Year's) is guarded with suppressHydrationWarning.
        const HYDRATION_ERROR = /Minified React error #4(?:1[89]|2[0-5])\b/;
        if (HYDRATION_ERROR.test(message)) {
          return;
        }

        const key = `${context}::${message}`;
        const now = Date.now();
        const last = reported.get(key);
        if (last && now - last < THROTTLE_MS) return;
        reported.set(key, now);

        void fetch("/api/error-report", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ context, message, stack }),
        }).catch(() => {
          // Best-effort; never let reporting throw.
        });
      } catch {
        // ignore
      }
    };

    const onError = (e: ErrorEvent) => {
      report("client.window-error", e.message, e.error?.stack || "");
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const message = reason instanceof Error ? reason.message : String(reason);
      const stack = reason instanceof Error ? reason.stack : "";
      report("client.unhandledrejection", message, stack);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // App mounted healthily — if we previously reloaded to recover a chunk
    // failure, clear the one-shot flag so a future transient failure in this
    // session can recover again.
    clearChunkReloadFlagAfter();

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
